// ===== Chat timeline canonical ingest owner =====
// classic script로 ws-render-helpers.js보다 먼저 로드된다. 렌더 함수들은 호출 시점에 해석된다.

type ChatTimelineToken = { sessionId: string; generation: number; anchorSeq?: number; signal?: AbortSignal };
type ChatTimelineRenderedAnchor = {
  sessionId?: string;
  seq?: number;
  clientMessageId?: string;
  provisional?: boolean;
};
type ChatTimelineIngestMode = "reset" | "prepend";
type ChatTimelineFetch = (input: string, init?: RequestInit) => Promise<Pick<Response, "ok" | "json">>;
type ChatTimelineCanonicalPayload = Record<string, unknown> & {
  sessionId: string;
  messages?: WsHistoryMessage[];
  hasMore?: boolean;
  hasBefore?: boolean;
  hasAfter?: boolean;
  targetSeq?: number;
  prepend?: boolean;
  offset?: number;
  ok?: boolean;
  logSeq?: number;
  ts?: string | number;
  text?: string;
  attachments?: ChatAttachment[];
  sender?: string;
  senderName?: string;
  senderAvatar?: string;
  clientMessageId?: string;
  replyTo?: ReplyQuoteView;
  artifact?: unknown;
  transfer?: TransferCardMeta;
};

function requireChatTimelineRoot(): HTMLElement {
  const element = document.getElementById("chatMessages");
  if (!(element instanceof HTMLElement)) throw new Error("Missing #chatMessages");
  return element;
}

// Phase 3(bigtionax-file-substrate-t2): canonical timeline의 단일 ingest/merge owner.
// 렌더러(addMessage/addPeerMessage/addAgentMessage/renderArtifactCard)는 DOM 생성만 계속 맡고,
// 이 객체가 session/generation/canonical key/provisional 승격/retention을 한 곳에서 판단한다.
class ChatTimelineNavigation {
  private readonly container: HTMLElement;
  private generation = 0;
  private sessionId: string | null = null;
  private activeAnchorSeq: number | null = null;
  private activeAnchorController: AbortController | null = null;
  // 스크롤업으로 되짚어 올라간 구간의 하한 seq. anchor(점프)와 달리 generation을 올리지 않는다 —
  // in-flight 요청을 무효화하면 안 되기 때문이다. retention에만 쓰인다.
  private retainedScrollbackSeq: number | null = null;
  private nodesByCanonicalKey = new Map<string, HTMLElement>();
  private provisionalByClientKey = new Map<string, HTMLElement>();

  constructor(container: HTMLElement) {
    this.container = container;
  }

  currentGeneration(): number {
    return this.generation;
  }

  currentAnchorSeq(): number | null {
    return this.activeAnchorSeq;
  }

  // 보존 중인 가장 오래된 canonical seq. 스크롤업이 "이 위"를 가리키는 좌표를 만들 때 쓴다.
  oldestRetainedSeq(): number | null {
    let oldest: number | null = null;
    for (const node of this.nodesByCanonicalKey.values()) {
      const seq = this.seqFromNode(node);
      if (!Number.isInteger(seq)) continue;
      if (oldest === null || (seq as number) < oldest) oldest = seq as number;
    }
    return oldest;
  }

  beginSession(sessionId: string): ChatTimelineToken {
    if (this.sessionId !== sessionId) {
      this.generation += 1;
      this.sessionId = sessionId;
      this.activeAnchorSeq = null;
      this.retainedScrollbackSeq = null;
      this.abortActiveAnchor();
      this.nodesByCanonicalKey.clear();
      this.provisionalByClientKey.clear();
    }
    return { sessionId, generation: this.generation };
  }

  beginAnchor(sessionId: string, anchorSeq: number): ChatTimelineToken {
    this.beginSession(sessionId);
    this.abortActiveAnchor();
    this.generation += 1;
    this.activeAnchorSeq = Number.isInteger(anchorSeq) && anchorSeq >= 0 ? anchorSeq : null;
    const controller = new AbortController();
    this.activeAnchorController = controller;
    return {
      sessionId,
      generation: this.generation,
      anchorSeq: this.activeAnchorSeq ?? undefined,
      signal: controller.signal,
    };
  }

  releaseAnchor(): void {
    if (this.activeAnchorSeq !== null || this.activeAnchorController !== null) {
      this.generation += 1;
      this.abortActiveAnchor();
    }
    this.activeAnchorSeq = null;
    this.retainedScrollbackSeq = null;
    this.trimRetained();
  }

  isCurrent(token: ChatTimelineToken): boolean {
    return this.sessionId === token.sessionId && this.generation === token.generation && token.signal?.aborted !== true;
  }

  resetToHistory(data: ChatTimelineCanonicalPayload, token?: ChatTimelineToken): boolean {
    if (!this.shouldAccept(data.sessionId, token)) return false;
    this.beginSession(data.sessionId);
    this.retainedScrollbackSeq = null;
    this.container.innerHTML = "";
    this.nodesByCanonicalKey.clear();
    this.provisionalByClientKey.clear();
    window.lastSender = null;

    for (const msg of capRenderMessages(data.messages || [])) {
      renderHistoryMessage(msg, data.sessionId);
    }
    this.trimRetained();
    this.container.scrollTop = this.container.scrollHeight;
    return true;
  }

  prependHistory(data: ChatTimelineCanonicalPayload, token?: ChatTimelineToken): boolean {
    if (!this.shouldAccept(data.sessionId, token)) return false;
    const messages = data.messages || [];
    if (messages.length === 0) return true;

    // 렌더 루프보다 먼저 하한을 내린다. registerRenderedNode가 노드마다 trimRetained()를 부르므로
    // (:264) 하한이 없으면 방금 prepend한 노드가 "최신 50" 밖이라 등록 즉시 제거되고, 그 뒤
    // node.parentElement 가드에 걸려 insertBefore조차 실행되지 않는다 — 스크롤업이 통째로 무력화된다.
    // anchor 유무로 갈리면 점프 상태의 스크롤업이 같은 방식으로 다시 버려진다 — 상태와 무관하게
    // 내린다. 하한은 더 아래로만 움직이고 session reset·최근 복귀에서만 풀린다.
    const batchSeqs = messages
      .map((message) => (message as { seq?: unknown }).seq)
      .filter((seq): seq is number => Number.isInteger(seq));
    if (batchSeqs.length > 0) {
      const batchFloor = Math.min(...batchSeqs);
      if (this.retainedScrollbackSeq === null || batchFloor < this.retainedScrollbackSeq) {
        this.retainedScrollbackSeq = batchFloor;
      }
    }

    const anchor = this.container.firstElementChild;
    const scrollHeightBefore = this.container.scrollHeight;
    const scrollTopBefore = this.container.scrollTop;
    // 총 높이 변화로 보정하면 trim 이 아래쪽 노드를 지운 만큼 상쇄돼 delta 가 0 에 가까워지고,
    // 화면은 위로 붙은 분량만큼 과거로 밀린다("이전 날짜로 건너뛴다"의 정체). 기준 노드의
    // 이동량으로 보정하면 어떤 제거가 일어나도 보던 메시지가 제자리를 지킨다.
    const anchorOffsetBefore = anchor instanceof HTMLElement ? anchor.offsetTop : null;
    const savedLastSender = window.lastSender;
    window.lastSender = null;

    for (const msg of messages) {
      const node = renderHistoryMessage(msg, data.sessionId);
      if (node instanceof HTMLElement && node.parentElement === this.container && anchor?.parentElement === this.container) {
        this.container.insertBefore(node, anchor);
      }
    }

    window.lastSender = savedLastSender;
    this.container.scrollTop = anchor instanceof HTMLElement && anchor.parentElement === this.container && anchorOffsetBefore !== null
      ? scrollTopBefore + (anchor.offsetTop - anchorOffsetBefore)
      : scrollTopBefore + (this.container.scrollHeight - scrollHeightBefore);
    this.trimRetained();
    return true;
  }

  async loadTargetWindow(sessionId: string, targetSeq: number, fetchImpl: ChatTimelineFetch = window.fetch.bind(window)): Promise<boolean> {
    if (!sessionId || !Number.isInteger(targetSeq) || targetSeq < 0) return false;
    const token = this.beginAnchor(sessionId, targetSeq);
    const url = `/api/sessions/${encodeURIComponent(sessionId)}/messages/window?seq=${encodeURIComponent(String(targetSeq))}`;
    try {
      const response = await fetchImpl(url, { signal: token.signal });
      if (!this.isCurrent(token) || !response.ok) return false;
      const body = await response.json();
      if (!this.isCurrent(token) || !isChatTimelineRecord(body) || body.ok !== true || body.sessionId !== sessionId) return false;
      if (body.targetSeq !== token.anchorSeq || !Array.isArray(body.messages)) return false;
      const historyPayload = {
        ...body,
        type: "session-history",
        sessionId,
        prepend: true,
        hasMore: !!body.hasBefore,
        offset: messageListState.offset,
      } as ChatTimelineCanonicalPayload;
      const accepted = ingestCanonicalMessages(historyPayload, "prepend", token);
      if (!accepted || !this.isCurrent(token)) return false;
      messageListState.loading = false;
      messageListState.hasMore = !!body.hasBefore;
      return true;
    } catch (error) {
      if (token.signal?.aborted === true || !this.isCurrent(token)) return false;
      console.warn("Failed to load target message window", error);
      messageListState.loading = false;
      return false;
    }
  }

  ingestRoomMessage(data: ChatTimelineCanonicalPayload): boolean {
    if (!this.shouldAccept(data.sessionId)) return false;
    if (this.isKnownCanonical(data.sessionId, data.logSeq)) return true;
    const timeStr = data.ts ? formatChatTimestamp(data.ts) : undefined;
    const displayText = stripAttachmentBlockForDisplay(data.text || "", data.attachments);
    const attribution = resolveHumanMessageAttribution(data);
    if (attribution.kind === "self") {
      addMessage("user", displayText, {
        time: timeStr,
        seq: data.logSeq,
        sessionId: data.sessionId,
        replyTo: data.replyTo,
        attachments: data.attachments,
        clientMessageId: data.clientMessageId,
      });
    } else {
      roomTypingRemove(attribution.senderName);
      addPeerMessage(
        attribution.senderName,
        attribution.senderAvatar,
        displayText,
        {
          time: timeStr,
          seq: data.logSeq,
          sessionId: data.sessionId,
          replyTo: data.replyTo,
          attachments: data.attachments,
          senderActorId: data.sender,
        },
      );
    }
    return true;
  }

  ingestArtifactCreated(data: ChatTimelineCanonicalPayload): boolean {
    if (!this.shouldAccept(data.sessionId)) return false;
    if (this.isKnownCanonical(data.sessionId, data.logSeq)) return true;
    renderArtifactCard(data.artifact, { seq: data.logSeq, sessionId: data.sessionId });
    return true;
  }

  ingestItemTransferred(data: ChatTimelineCanonicalPayload): boolean {
    if (!this.shouldAccept(data.sessionId)) return false;
    if (this.isKnownCanonical(data.sessionId, data.logSeq)) return true;
    renderArtifactCard(data.artifact, {
      transfer: data.transfer,
      seq: data.logSeq,
      sessionId: data.sessionId,
    });
    return true;
  }

  registerRenderedNode(node: HTMLElement | null | undefined, anchor: ChatTimelineRenderedAnchor = {}): HTMLElement | null | undefined {
    if (!node || !node.dataset) return node;
    const sessionId = anchor.sessionId || node.dataset.sessionId;
    const seq = Number.isInteger(anchor.seq) ? anchor.seq : this.seqFromNode(node);
    const clientMessageId = anchor.clientMessageId || node.dataset.clientMessageId;

    if (sessionId && clientMessageId && (anchor.provisional || node.dataset.provisional === "true")) {
      this.provisionalByClientKey.set(this.clientKey(sessionId, clientMessageId), node);
    }

    if (!sessionId || !Number.isInteger(seq) || (seq as number) < 0) return node;
    const canonicalKey = this.canonicalKey(sessionId, seq as number);
    const provisional = clientMessageId ? this.provisionalByClientKey.get(this.clientKey(sessionId, clientMessageId)) : null;
    const existing = this.nodesByCanonicalKey.get(canonicalKey);

    if (provisional && provisional !== node) {
      const stableClientMessageId = clientMessageId as string;
      applyMessageAnchor(provisional, { sessionId, seq: seq as number, clientMessageId });
      delete provisional.dataset.provisional;
      this.nodesByCanonicalKey.set(canonicalKey, provisional);
      this.provisionalByClientKey.delete(this.clientKey(sessionId, stableClientMessageId));
      node.remove();
      if (typeof appendReactionSlot === "function") appendReactionSlot(provisional);
      this.placeCanonicalNode(provisional);
      this.trimRetained();
      return provisional;
    }

    if (existing && existing !== node) {
      node.remove();
      return existing;
    }

    this.nodesByCanonicalKey.set(canonicalKey, node);
    if (clientMessageId) this.provisionalByClientKey.delete(this.clientKey(sessionId, clientMessageId));
    this.placeCanonicalNode(node);
    this.trimRetained();
    return node;
  }

  reconcileAck({ clientMessageId, sessionId, logSeq, replyTo }: {
    clientMessageId: string;
    sessionId: string;
    logSeq?: number;
    replyTo?: ReplyQuoteView;
  }): boolean {
    if (!clientMessageId || !sessionId) return false;
    const clientKey = this.clientKey(sessionId, clientMessageId);
    const node = this.provisionalByClientKey.get(clientKey)
      || this.container.querySelector<HTMLElement>(`.message[data-client-message-id="${clientMessageId}"]`);
    if (!node || node.dataset.sessionId !== sessionId) return false;
    if (Number.isInteger(logSeq) && (logSeq as number) >= 0) {
      const canonicalKey = this.canonicalKey(sessionId, logSeq as number);
      const existing = this.nodesByCanonicalKey.get(canonicalKey);
      if (existing && existing !== node) {
        node.remove();
        this.provisionalByClientKey.delete(clientKey);
        return true;
      }
      node.dataset.msgSeq = String(logSeq);
      node.dataset.unreadCountEligible = "true";
      this.nodesByCanonicalKey.set(canonicalKey, node);
      if (typeof appendReactionSlot === "function") appendReactionSlot(node);
    }
    delete node.dataset.provisional;
    this.provisionalByClientKey.delete(clientKey);
    if (replyTo) {
      const bubble = node.querySelector<HTMLElement>(".bubble");
      if (bubble && !bubble.querySelector(".quote")) {
        bubble.insertAdjacentHTML("afterbegin", renderQuoteBlock(replyTo));
      }
    }
    if (typeof MessageReadReceipts !== "undefined") MessageReadReceipts.onMessageRendered(node);
    this.placeCanonicalNode(node);
    this.trimRetained();
    return true;
  }

  private shouldAccept(sessionId: string | undefined, token?: ChatTimelineToken): boolean {
    if (!sessionId) return false;
    if (token && !this.isCurrent(token)) return false;
    return this.sessionId === null || this.sessionId === sessionId || this.beginSession(sessionId).sessionId === sessionId;
  }

  private abortActiveAnchor(): void {
    if (this.activeAnchorController !== null) {
      this.activeAnchorController.abort();
      this.activeAnchorController = null;
    }
  }

  private isKnownCanonical(sessionId: string, seq: number | undefined): boolean {
    return Number.isInteger(seq) && (seq as number) >= 0 && this.nodesByCanonicalKey.has(this.canonicalKey(sessionId, seq as number));
  }

  private canonicalKey(sessionId: string, seq: number): string {
    return `${sessionId}:${seq}`;
  }

  private clientKey(sessionId: string, clientMessageId: string): string {
    return `${sessionId}:${clientMessageId}`;
  }

  private seqFromNode(node: HTMLElement): number | undefined {
    const raw = node.dataset.msgSeq;
    if (raw === undefined) return undefined;
    const seq = Number(raw);
    return Number.isInteger(seq) && seq >= 0 ? seq : undefined;
  }

  private placeCanonicalNode(node: HTMLElement): void {
    const seq = this.seqFromNode(node);
    const sessionId = node.dataset.sessionId;
    if (!sessionId || !Number.isInteger(seq)) return;
    for (const child of Array.from(this.container.children)) {
      if (!(child instanceof HTMLElement) || child === node) continue;
      if (child.dataset.sessionId !== sessionId) continue;
      const childSeq = this.seqFromNode(child);
      if (Number.isInteger(childSeq) && (childSeq as number) > (seq as number)) {
        this.container.insertBefore(node, child);
        return;
      }
    }
  }

  private trimRetained(): void {
    const entries = Array.from(this.nodesByCanonicalKey.entries())
      .map(([key, node]) => ({ key, node, seq: this.seqFromNode(node) }))
      .filter((entry): entry is { key: string; node: HTMLElement; seq: number } => Number.isInteger(entry.seq));
    if (entries.length <= 50 && this.activeAnchorSeq === null && this.retainedScrollbackSeq === null) return;
    const sorted = entries.sort((a, b) => a.seq - b.seq);
    const keep = new Set<string>();
    for (const entry of sorted.slice(-50)) keep.add(entry.key);
    if (this.activeAnchorSeq !== null) {
      const anchorIndex = Math.max(0, sorted.findIndex((entry) => entry.seq >= (this.activeAnchorSeq as number)));
      const start = Math.max(0, anchorIndex - 25);
      for (const entry of sorted.slice(start, start + 50)) keep.add(entry.key);
    }
    if (this.retainedScrollbackSeq !== null) {
      // 스크롤업으로 되짚은 구간은 연속으로 남긴다. 100(=배치 2개)로 잡으면 세 번째 스크롤업에서
      // 바로 아래 50건이 사라져 제거가 시야 근처에서 일어난다 — 넉넉히 잡아 제거 지점을 화면에서
      // 멀리 둔다. 상한은 tail 50 과 합쳐 350 노드다.
      const floorIndex = Math.max(0, sorted.findIndex((entry) => entry.seq >= (this.retainedScrollbackSeq as number)));
      for (const entry of sorted.slice(floorIndex, floorIndex + 300)) keep.add(entry.key);
    }
    for (const entry of sorted) {
      if (keep.has(entry.key)) continue;
      entry.node.remove();
      this.nodesByCanonicalKey.delete(entry.key);
    }
  }
}

function isChatTimelineRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const chatTimelineNavigation = new ChatTimelineNavigation(requireChatTimelineRoot());

function ingestCanonicalMessages(data: ChatTimelineCanonicalPayload, mode: ChatTimelineIngestMode = "reset", token?: ChatTimelineToken): boolean {
  return mode === "prepend"
    ? chatTimelineNavigation.prependHistory(data, token)
    : chatTimelineNavigation.resetToHistory(data, token);
}
